/**
 * Fetch wrapper — jediná cesta webu k API. Vždy `credentials: 'include'`
 * (httpOnly cookie `helfr_session`), jednotné zpracování chyb:
 *  - 401 → callback `pri401` (přesměrování na login, mimo samotný login),
 *  - 4xx/5xx → ApiChyba s lidskou hláškou z pole `error` odpovědi API.
 */

export class ApiChyba extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Celé tělo chybové odpovědi (např. 422 importu nese pole `chyby`). */
    public readonly telo?: unknown,
  ) {
    super(message);
    this.name = 'ApiChyba';
  }
}

/** Lidská hláška z libovolné chyby — pro zobrazení v UI. */
export function hlaskaZChyby(err: unknown): string {
  if (err instanceof ApiChyba) return err.message;
  if (err instanceof Error) return `Nepodařilo se spojit se serverem (${err.message}).`;
  return 'Neznámá chyba — zkuste to prosím znovu.';
}

let pri401: (() => void) | null = null;

/** Registrace reakce na 401 (odhlášená session) — nastavuje AuthProvider. */
export function nastavPri401(handler: (() => void) | null): void {
  pri401 = handler;
}

async function request<T>(cesta: string, init?: RequestInit): Promise<T> {
  const res = await fetch(cesta, { credentials: 'include', ...init });
  let telo: unknown = null;
  try {
    telo = await res.json();
  } catch {
    telo = null; // prázdná / ne-JSON odpověď
  }
  if (!res.ok) {
    if (res.status === 401 && !cesta.startsWith('/api/auth/login')) pri401?.();
    const error =
      telo !== null && typeof telo === 'object' && 'error' in telo
        ? String((telo as { error: unknown }).error)
        : `Chyba serveru (HTTP ${res.status}).`;
    throw new ApiChyba(res.status, error, telo);
  }
  return telo as T;
}

const json = (data: unknown): RequestInit => ({
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(data),
});

export const api = {
  get: <T>(cesta: string) => request<T>(cesta),
  post: <T>(cesta: string, data: unknown) => request<T>(cesta, { method: 'POST', ...json(data) }),
  patch: <T>(cesta: string, data: unknown) => request<T>(cesta, { method: 'PATCH', ...json(data) }),
  put: <T>(cesta: string, data: unknown) => request<T>(cesta, { method: 'PUT', ...json(data) }),
  del: <T>(cesta: string) => request<T>(cesta, { method: 'DELETE' }),
  /** Multipart upload (import soupisu prací) — bez content-type, nastaví prohlížeč. */
  upload: <T>(cesta: string, form: FormData) => request<T>(cesta, { method: 'POST', body: form }),
};
